import { Dialog } from "radix-ui";
import type { ReactNode } from "react";
import { usePrimitiveT } from "../i18n/I18nProvider.tsx";
import { IconButton } from "./IconButton.tsx";

export type SheetProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  detents?: ("medium" | "large")[];
  /** Label for the close button (translated; default `common.close` in the app's locale). */
  closeLabel?: string;
  /**
   * "side": a full-height panel on the trailing edge (row peek) on wide layouts; phones keep the bottom sheet.
   * "bottom": docked to the bottom edge at every width (pickers opened from the composer on phones).
   */
  placement?: "center" | "side" | "bottom";
  /** "large": a wide, tall centered dialog for galleries (template gallery); phones keep a full-width sheet. */
  size?: "default" | "large";
  /** Controls in the header before the close button (e.g. "Open as page"). */
  headerActions?: ReactNode;
  /** The sheet; its close button is `<testID>-close` (`sheet-close` without a testID). */
  testID?: string;
  children?: ReactNode;
};

/** Modal sheet: centered dialog on web/desktop, bottom sheet with detents on phones. */
export function Sheet({ open, onClose, title, detents, closeLabel, placement = "center", size = "default", headerActions, testID, children }: SheetProps) {
  const t = usePrimitiveT();
  const close = closeLabel ?? t("common.close");
  return (
    <Dialog.Root open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="b-sheet-overlay" />
        <Dialog.Content
          className={
            placement === "side"
              ? "b-sheet b-sheet-side"
              : placement === "bottom"
                ? "b-sheet b-sheet-bottom"
                : size === "large"
                  ? "b-sheet b-sheet-large"
                  : detents !== undefined && detents.length === 1 && detents[0] === "medium"
                    ? "b-sheet b-sheet-medium"
                    : "b-sheet"
          }
          data-testid={testID}
          aria-describedby={undefined}
        >
          <div className="b-sheet-header">
            {title === undefined ? <Dialog.Title className="b-visually-hidden">{close}</Dialog.Title> : <Dialog.Title className="b-sheet-title">{title}</Dialog.Title>}
            <div className="b-sheet-actions">
              {headerActions}
              <IconButton icon="close" accessibilityLabel={close} onPress={onClose} testID={testID === undefined ? "sheet-close" : `${testID}-close`} size="sm" />
            </div>
          </div>
          <div className="b-sheet-body">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
